import React from "react";
import clsx from "clsx";
import Paper from "@material-ui/core/Paper";
import IconButton from "@material-ui/core/IconButton";
import { Box, Typography, Divider, Menu, MenuItem, ListItemIcon, ListItemText } from "@material-ui/core";
import LockIcon from "@material-ui/icons/Lock";
import AddIcon from "@material-ui/icons/Add";
import MoodIcon from "@material-ui/icons/Mood";
import DescriptionIcon from "@material-ui/icons/Description";
import PhotoCameraIcon from "@material-ui/icons/PhotoCamera";
import MicIcon from "@material-ui/icons/Mic";
import ContactPhoneIcon from "@material-ui/icons/ContactPhone";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import FolderIcon from "@material-ui/icons/Folder";
import ClearIcon from "@material-ui/icons/Clear";
import { useTheme } from "@material-ui/core/styles";
import useMessageInputStyles from "../messageInputStyles";
import EmojiOptions from "./EmojiOptions";
import FileInput from "./FileInput";
import SignSwitch from "./SignSwitch";
import CustomInput from "./CustomInput";
import ActionButtons from "./ActionButtons";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import { getChannelCapabilities } from "../../../constants/whatsappProviders";

const NormalInputView = (props) => {
  const {
    blocked,
    isPending,
    isAIBlocked,
    replyingMessage,
    setReplyingMessage,
    isMobileCompact,
    width,
    isDragOver,
    handleDragOver,
    handleDragEnter,
    handleDragLeave,
    handleDrop,
    mobileFabRef,
    mobileComposerMenuAnchor,
    setMobileComposerMenuAnchor,
    closeMobileComposerMenu,
    mobilePickDocument,
    mobilePickPhotoVideo,
    mobilePickAudio,
    mobilePickContact,
    showEmoji,
    setShowEmoji,
    handleAddEmoji,
    disableOption,
    handleChangeMedias,
    handleQuickReplyButtonClick,
    handleFileListButtonClick,
    handleFileListMenuClose,
    handleFileListSelect,
    fileListAnchorEl,
    fileListOptions,
    signMessage,
    setSignMessage,
    loading,
    sendingText,
    recording,
    inputMessage,
    setInputMessage,
    inputRef,
    ticketStatus,
    handleSendMessage,
    handleInputPaste,
    handleQuickAnswersClick,
    options,
    popupOpen,
    handleCancelAudio,
    handleUploadAudio,
    handleStartRecording,
    analyserRef,
    handleOpenModalForward,
    showSelectMessageCheckbox,
    setShowSelectMessageCheckbox,
    setSelectedMessages,
    capabilities = getChannelCapabilities(),
    emailTo,
  } = props;

  const classes = useMessageInputStyles();
  const theme = useTheme();

  const renderReplyingMessage = (message) => {
    return (
      <div className={classes.replyginMsgWrapper}>
        <div className={classes.replyginMsgContainer}>
          <span
            className={clsx(classes.replyginContactMsgSideColor, {
              [classes.replyginSelfMsgSideColor]: !message.fromMe,
            })}
          ></span>
          <div className={classes.replyginMsgBody}>
            {!message.fromMe && (
              <span className={classes.messageContactName}>
                {message.contact?.name}
              </span>
            )}
            {message.body}
          </div>
        </div>
        <IconButton
          aria-label="showRecorder"
          component="span"
          disabled={loading || ticketStatus !== "open"}
          onClick={() => setReplyingMessage(null)}
        >
          <ClearIcon className={classes.sendMessageIcons} />
        </IconButton>
      </div>
    );
  };

  return (
    <Paper
      square
      elevation={0}
      className={classes.mainWrapper}
      style={{
        opacity: blocked ? 0.5 : 1,
        pointerEvents: blocked ? "none" : "auto",
        position: "relative",
      }}
    >
      {/* Mensaje de modo lectura cuando ticket está pending */}
      {isPending && (
        <Box
          style={{
            padding: "12px 16px",
            backgroundColor: theme.mode === "dark" ? "#1f2937" : "#fff3cd",
            borderTop: `1px solid ${theme.mode === "dark" ? "#4B5563" : "#ffc107"}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            marginBottom: "8px",
          }}
        >
          <LockIcon
            style={{
              color: theme.mode === "dark" ? "#fbbf24" : "#856404",
              fontSize: "18px",
            }}
          />
          <Typography
            variant="body2"
            style={{
              color: theme.mode === "dark" ? "#fbbf24" : "#856404",
              fontWeight: "500",
              fontSize: "14px",
            }}
          >
            Modo lectura: Este ticket está pendiente. Debes aceptarlo para poder responder.
          </Typography>
        </Box>
      )}

      {/* Mensaje de bloqueo cuando ticket tiene IA */}
      {isAIBlocked && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            borderRadius: "8px",
            border: "2px dashed #ff9800",
          }}
        >
          <div
            style={{
              textAlign: "center",
              color: "#ff9800",
              fontWeight: "bold",
              fontSize: "14px",
            }}
          >
            <span role="img" aria-label="robot">🤖</span> Este ticket está siendo atendido por un AGENTE IA
            <br />
            <span style={{ fontSize: "12px", fontWeight: "normal" }}>
              Los humanos no pueden intervenir en esta conversación
            </span>
          </div>
        </div>
      )}

      {replyingMessage && renderReplyingMessage(replyingMessage)}

      {/* Quien escribe aquí redacta un CORREO, no un chat: a quién va y que sigue el hilo. */}
      {capabilities.thread && emailTo && !isPending && (
        <Box style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 16px 0", fontSize: 12, color: theme.palette.text.secondary }}>
          <MailOutlineIcon style={{ fontSize: 15 }} />
          <span>
            Respondes por correo a <b>{emailTo}</b>
            {replyingMessage ? ", en respuesta al correo citado" : ", en el mismo hilo"}
          </span>
        </Box>
      )}

      <div
        className={clsx(
          classes.newMessageBox,
          isMobileCompact && classes.newMessageBoxCompact
        )}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        style={{
          border: isDragOver ? "2px dashed #2196f3" : undefined,
          backgroundColor: isDragOver ? "#f0f8ff" : undefined,
          transition: "all 0.3s ease",
          position: "relative",
        }}
      >
        {/* Indicador de drag & drop */}
        {isDragOver && (
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              background: "rgba(33, 150, 243, 0.9)",
              color: "white",
              padding: "20px",
              borderRadius: "10px",
              zIndex: 1000,
              pointerEvents: "none",
              fontSize: "16px",
              fontWeight: "bold",
              boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
            }}
          >
            <span role="img" aria-label="carpeta">📁</span> SUELTA AQUÍ PARA ADJUNTAR ARCHIVOS - NUEVO
          </div>
        )}

        {/* Móvil (sm-): un solo botón + con menú */}
        {isMobileCompact && (
          <>
            <IconButton
              ref={mobileFabRef}
              aria-label="herramientas de mensaje"
              component="span"
              disabled={disableOption()}
              className={classes.mobileComposerFab}
              onClick={(e) => setMobileComposerMenuAnchor(e.currentTarget)}
              title="Emojis, adjuntos y más"
            >
              <AddIcon
                style={{ color: theme.mode === "dark" ? "white" : "#2196f3" }}
              />
            </IconButton>
            <Menu
              anchorEl={mobileComposerMenuAnchor}
              open={Boolean(mobileComposerMenuAnchor)}
              onClose={closeMobileComposerMenu}
              PaperProps={{
                style: {
                  backgroundColor: theme.palette.background.paper,
                  borderRadius: "12px",
                  minWidth: 240,
                  maxHeight: "70vh",
                  border: `1px solid ${
                    theme.mode === "dark" ? "#4B5563" : "#e0e0e0"
                  }`,
                  boxShadow:
                    theme.mode === "dark"
                      ? "0 4px 20px rgba(0,0,0,0.5)"
                      : "0 4px 20px rgba(0,0,0,0.15)",
                },
              }}
              anchorOrigin={{ vertical: "top", horizontal: "left" }}
              transformOrigin={{ vertical: "bottom", horizontal: "left" }}
            >
              <MenuItem
                onClick={() => {
                  setShowEmoji((p) => !p);
                  closeMobileComposerMenu();
                }}
              >
                <ListItemIcon>
                  <MoodIcon />
                </ListItemIcon>
                <ListItemText primary="Emojis" />
              </MenuItem>
              <Divider />
              <MenuItem onClick={mobilePickDocument}>
                <ListItemIcon>
                  <DescriptionIcon style={{ color: "#9c27b0" }} />
                </ListItemIcon>
                <ListItemText primary="Documentos" />
              </MenuItem>
              <MenuItem onClick={mobilePickPhotoVideo}>
                <ListItemIcon>
                  <PhotoCameraIcon style={{ color: "#2196f3" }} />
                </ListItemIcon>
                <ListItemText primary="Fotos y Videos" />
              </MenuItem>
              {capabilities.audio && (
              <MenuItem onClick={mobilePickAudio}>
                <ListItemIcon>
                  <MicIcon style={{ color: "#ff9800" }} />
                </ListItemIcon>
                <ListItemText primary="Audio" />
              </MenuItem>
              )}
              {capabilities.richAttachments && (
              <MenuItem onClick={() => { closeMobileComposerMenu(); if (props.onContactPick) props.onContactPick(); }}>
                <ListItemIcon>
                  <ContactPhoneIcon style={{ color: "#4caf50" }} />
                </ListItemIcon>
                <ListItemText primary="Contactos" />
              </MenuItem>
              )}
              <Divider />
              <MenuItem
                onClick={() => {
                  closeMobileComposerMenu();
                  handleQuickReplyButtonClick();
                }}
              >
                <ListItemIcon>
                  <FlashOnIcon />
                </ListItemIcon>
                <ListItemText primary="Respuestas rápidas" />
              </MenuItem>
              <MenuItem
                onClick={() => {
                  closeMobileComposerMenu();
                  setTimeout(() => {
                    if (mobileFabRef.current) {
                      handleFileListButtonClick({
                        currentTarget: mobileFabRef.current,
                      });
                    }
                  }, 50);
                }}
              >
                <ListItemIcon>
                  <FolderIcon />
                </ListItemIcon>
                <ListItemText primary="Listas de archivos" />
              </MenuItem>
            </Menu>
          </>
        )}

        <EmojiOptions
          disabled={disableOption()}
          handleAddEmoji={handleAddEmoji}
          showEmoji={showEmoji}
          setShowEmoji={setShowEmoji}
          hideButton={isMobileCompact}
        />

        <FileInput
          disableOption={disableOption}
          handleChangeMedias={handleChangeMedias}
          hideTriggerButton={isMobileCompact}
          onContactPick={props.onContactPick}
          allowAudio={capabilities.audio}
          allowContact={capabilities.richAttachments}
        />

        {!isMobileCompact && (
          <>
            <IconButton
              aria-label="quickReplies"
              component="span"
              disabled={disableOption()}
              onClick={handleQuickReplyButtonClick}
              title="Respuestas Rápidas"
            >
              <FlashOnIcon className={classes.sendMessageIcons} />
            </IconButton>

            <IconButton
              aria-label="fileLists"
              component="span"
              disabled={disableOption()}
              onClick={handleFileListButtonClick}
              title="Listas de Archivos"
            >
              <FolderIcon className={classes.sendMessageIcons} />
            </IconButton>
          </>
        )}

        <Menu
          anchorEl={fileListAnchorEl}
          open={Boolean(fileListAnchorEl)}
          onClose={handleFileListMenuClose}
          PaperProps={{
            style: {
              maxHeight: 300,
              width: 300,
              backgroundColor:
                theme.mode === "dark" ? "#1f2937" : "#ffffff",
              border: `1px solid ${
                theme.mode === "dark" ? "#4B5563" : "#e0e0e0"
              }`,
              boxShadow:
                theme.mode === "dark"
                  ? "0 4px 20px rgba(0,0,0,0.5)"
                  : "0 4px 20px rgba(0,0,0,0.15)",
            },
          }}
        >
          {fileListOptions.map((fileList) => (
            <MenuItem
              key={fileList.value.id}
              onClick={() => handleFileListSelect(fileList)}
              style={{
                display: "flex",
                alignItems: "center",
                fontSize: "14px",
                padding: "8px 16px",
                color: theme.mode === "dark" ? "#f9fafb" : "#1f2937",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor =
                  theme.mode === "dark" ? "#374151" : "#f5f5f5";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              {fileList.label}
            </MenuItem>
          ))}
          {fileListOptions.length === 0 && (
            <MenuItem
              disabled
              style={{
                color: theme.mode === "dark" ? "#6b7280" : "#9e9e9e",
              }}
            >
              No hay listas de archivos disponibles
            </MenuItem>
          )}
        </Menu>

        <SignSwitch
          width={width}
          setSignMessage={setSignMessage}
          signMessage={signMessage}
        />

        <CustomInput
          loading={loading}
          inputRef={inputRef}
          ticketStatus={ticketStatus}
          inputMessage={inputMessage}
          setInputMessage={setInputMessage}
          handleSendMessage={handleSendMessage}
          handleInputPaste={handleInputPaste}
          disableOption={disableOption}
          handleQuickAnswersClick={handleQuickAnswersClick}
          options={options}
          popupOpen={popupOpen}
          inputWrapperClassName={
            isMobileCompact ? classes.messageInputWrapperGrow : undefined
          }
        />

        <ActionButtons
          inputMessage={inputMessage}
          loading={loading}
          sendingText={sendingText}
          recording={recording}
          analyserRef={analyserRef}
          ticketStatus={ticketStatus}
          handleSendMessage={handleSendMessage}
          handleCancelAudio={handleCancelAudio}
          handleUploadAudio={handleUploadAudio}
          handleStartRecording={handleStartRecording}
          handleOpenModalForward={handleOpenModalForward}
          showSelectMessageCheckbox={showSelectMessageCheckbox}
          setShowSelectMessageCheckbox={setShowSelectMessageCheckbox}
          setSelectedMessages={setSelectedMessages}
          allowAudio={capabilities.audio}
        />
      </div>
    </Paper>
  );
};

export default NormalInputView;
